import { useState, useMemo } from "react";
import {
	SmartFieldQuestionnaireBase,
	filterSmartFieldOptionForSubmittedRFQ,
} from "./SmartFieldQuestionnaireBase";
import { useSmartFieldSelection } from "../hooks/useSmartFieldSelection";
import {
	KeyPersonnel,
	KeyPersonnelSkeleton,
} from "pages/RFQ/RFQ/CreateRFQ/PreviewRFQ/Questionnaire/_components/KeyPersonnel";
import { useRfxSubmissionData } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";
import { useCurrentCompanyId } from "hooks/auth";
import { LoadMoreCard } from "./LoadMoreCard";
import { AddNewCard } from "./AddNewCard";
import { AddNewKeyPersonnelModal } from "./AddNewKeyPersonnelModal";
import { KeyPersonnelTableView } from "./KeyPersonnelTableView";
import { useKeyPersonnelPaginatedList } from "newui/features/Company/KeyPersonnel/hooks/useKeyPersonnel";
import { RfxQuestion } from "newui/features/Rfx/types/returnable-schedule-question.type";
import type { ViewMode } from "./SmartFieldQuestionnaireBase";
import type {
	KeyPersonnelDetail,
	KeyPersonnelFilterSelection,
	KeyPersonnelListParams,
} from "newui/features/Company/KeyPersonnel/types/KeyPersonnel.type";

interface SmartFieldKeyPersonnelProps {
	name: string;
	[key: string]: unknown;
	answer?: {
		selectedKeyPersonnel?: KeyPersonnelDetail[];
	};
	className?: string;
	disabled?: boolean;
	isClientPreview?: boolean;
	question: RfxQuestion;
	required?: boolean;
}

const keyPersonnelDummyData: KeyPersonnelDetail = {
	id: 0,
	first_name: "Example",
	last_name: "Person Card",
	full_name: "Example Person Card",
	position: "Their Position",
	experience: 0,
	email: "",
	company_id: 0,
	sort_order: null,
	avatar_location: null,
	sectors: [],
	disciplines: [],
	disciplinesFormatted: [],
	projects_completed: [
		{
			name: "Their Last Project",
			sector_id: null,
			sector_ids: [],
			role: "",
			location: null,
			value: "",
			value_humanized: "",
			images: [],
			sector: null,
			sectors: [],
			isCompanyProject: false,
			project_id: null,
		},
	],
	resume: [],
	is_resume_private: false,
};

export const SmartFieldKeyPersonnel: React.FC<SmartFieldKeyPersonnelProps> = ({
	className,
	question,
	required,
	isClientPreview,
	answer,
	...props
}) => {
	const [search, setSearch] = useState("");
	const [selectedFilter, setSelectedFilter] = useState<KeyPersonnelFilterSelection>({});
	const [showAddModal, setShowAddModal] = useState(false);
	const [viewMode, setViewMode] = useState<ViewMode>("card");
	const companyId = useCurrentCompanyId();
	const { rfxId, isClientView } = useRfxSubmissionData();
	const { value, error, touched, isSelected, selectHandler, isSubmitting } =
		useSmartFieldSelection(props, { shouldSubmit: true });

	// Capture initial selections so selected items always appear (stable ref — won't trigger refetch)
	const [initialIncludeIds] = useState<number[]>(() =>
		Array.isArray(value) ? value : value ? [value] : []
	);

	const queryParams = useMemo<KeyPersonnelListParams>(
		() => ({
			search,
			filters: selectedFilter,
			replacement: 1,
			rfq_id: rfxId,
			...(initialIncludeIds.length > 0 && { include_ids: initialIncludeIds }),
		}),
		[search, selectedFilter, rfxId, initialIncludeIds],
	);

	const useAnswerData =
		(props?.disabled && answer?.selectedKeyPersonnel) || !rfxId || isClientView;

	const {
		data,
		isLoading,
		error: queryError,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isRefetching,
		refetch,
	} = useKeyPersonnelPaginatedList(queryParams, !useAnswerData && !isClientPreview);

	// Flatten paginated results
	const allPages = data?.pages ?? [];
	const paginatedPersonnel = allPages.flatMap(
		(page) => page.key_personnels ?? []
	);
	const includedItems = allPages[0]?.included_items ?? [];
	const filteringList = allPages[0]?.filters ?? [];

	// Merge paginated + included items (deduplicated)
	const mergedPersonnel = [...paginatedPersonnel];
	includedItems.forEach((item) => {
		if (!mergedPersonnel.some((p) => p.id === item.id)) {
			mergedPersonnel.push(item);
		}
	});

	const keyPersonnels =
		mergedPersonnel.length > 0
			? mergedPersonnel
			: (answer?.selectedKeyPersonnel ?? []);

	// Filter data for preview mode
	const filteredData = filterSmartFieldOptionForSubmittedRFQ(
		keyPersonnels,
		value,
		props?.disabled
	);

	const selectedIds = Array.isArray(value) ? value : value ? [value] : [];
	const selectedData = keyPersonnels.filter((person) =>
		selectedIds.includes(person.id)
	);

	const pagination = allPages[allPages.length - 1]?.pagination;
	const remaining = pagination
		? pagination.total - paginatedPersonnel.length
		: 0;

	if (isClientPreview) {
		filteredData.push(keyPersonnelDummyData);
	}

	const handlePersonnelCreated = (newId: number): void => {
		selectHandler(newId);
	};

	const showAddNew = !isClientPreview && !isClientView;
	// `showAddNew` distinguishes the bidder's own view from a client preview; it
	// says nothing about whether the field is editable. A disabled field must
	// offer no Add affordance at all -- previously only the inline card threaded
	// `disabled` through, leaving the header button visible AND clickable.
	const canAddNew = showAddNew && !props?.disabled;
	const hasItems = mergedPersonnel.length > 0;

	return (
		<SmartFieldQuestionnaireBase
			{...props}
			className={className}
			question={question}
			required={required}
			description="Please choose key personnel that might work on this project."
			searchPlaceholder="Search name, position or project"
			isLoading={isLoading}
			loadingMessage="Loading your key personnel..."
			filteringList={filteringList as unknown as never}
			selectedFilter={selectedFilter as unknown as never}
			setSelectedFilter={setSelectedFilter as unknown as never}
			setSearch={setSearch}
			error={error}
			touched={touched}
			type="key-personnel"
			selectedData={selectedData as unknown as Record<string, unknown>[]}
			showFilter={!props?.disabled}
			showSearch={!props?.disabled}
			isClientPreview={isClientPreview}
			onAddNew={canAddNew && hasItems ? () => setShowAddModal(true) : undefined}
			addNewLabel={canAddNew && hasItems ? "Add Key Personnel" : undefined}
			onRefresh={showAddNew ? () => refetch() : undefined}
			isRefreshing={isRefetching}
			viewMode={viewMode}
			onViewModeChange={setViewMode}
			tableContent={
				filteredData.length > 0 ? (
					<>
						<KeyPersonnelTableView
							items={filteredData}
							selectedIds={selectedIds}
							onSelect={selectHandler}
							companyId={companyId ?? undefined}
							isPreview={props?.disabled}
							isSubmitting={isSubmitting}
						/>
						{hasNextPage && !props?.disabled && (
							<div className="mt-3 flex justify-center">
								<button
									type="button"
									onClick={() => fetchNextPage()}
									disabled={isFetchingNextPage}
									className="flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-600 transition-colors hover:border-blue-400 hover:text-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
								>
									{isFetchingNextPage ? "Loading..." : `Load More (${remaining})`}
								</button>
							</div>
						)}
					</>
				) : undefined
			}
			emptyStateContent={canAddNew && !hasItems && !isLoading ? (
				<div className="mt-4 w-full">
					<p className="mb-2 text-sm italic text-gray-500">
						Please add new key personnel to your profile, to submit with your response. Once added here, they will be available for any future submissions.
					</p>
					<AddNewCard
						label="+ Add Key Personnel"
						onClick={() => setShowAddModal(true)}
					/>
				</div>
			) : undefined}
		>
			{isLoading ? (
				[...Array(3)].map((_, index) => <KeyPersonnelSkeleton key={index} />)
			) : queryError ? (
				<div className="text-red-500">
					Error loading personnel: {queryError.message || "Unknown error"}
				</div>
			) : filteredData.length === 0 && !canAddNew ? (
				<div className="italic text-gray-500">
					No personnel {useAnswerData ? "selected" : "found"}
				</div>
			) : (
				<>
					{filteredData.map((personnel) => (
						<KeyPersonnel
							key={personnel.id}
							companyId={companyId}
							values={personnel}
							rfqSubmission={true}
							selected={isSelected(personnel?.id)}
							selectHandler={() => selectHandler(personnel?.id)}
							isPreview={props?.disabled}
							isClientPreview={isClientPreview}
							supplierSmartField
							isSubmitting={isSubmitting}
						/>
					))}
					{hasNextPage && !props?.disabled && (
						<LoadMoreCard
							onClick={() => fetchNextPage()}
							isLoading={isFetchingNextPage}
							remaining={remaining}
						/>
					)}
				</>
			)}
			{showAddModal && (
				<AddNewKeyPersonnelModal
					isOpen={showAddModal}
					onClose={() => setShowAddModal(false)}
					onCreated={handlePersonnelCreated}
				/>
			)}
		</SmartFieldQuestionnaireBase>
	);
};
